import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormDatePickerInput } from "../../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { CentroCustoDropDown } from "../../../../../../common/domain/models/centro_custo_dropdown";
import { useCallback, useEffect } from "react";
import { FormMoneyMaskedInput } from "../../../../../../common/components/masked_inputs/react_forms/form_money_masked_input";
import { LogTipoTabela } from "../../../../../../common/utils/log_tabela_tipo";
import { isNotNullOrUndefined } from "../../../../../../common/utils/utils";
import { EntradaProduto } from "../../../../domain/models/entrada_produtos_lista";
import { EntradaProdutoForm, EntradaProdutoSchema } from "../../../schemas/Entrada_Produtos/entrada_produtos";
import { DropDownProdutoServico } from "../../../../../../common/components/dropdown_produtoservico";
import { DropDownFornecedor } from "../../../../../../common/components/dropdown_fornecedor";
import { ProdutoServicoDropDown } from "../../../../../../common/domain/models/produto_servico_dropdown";

import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import PrimaryButton from "../../../../../../common/components/primary_button";

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  selectedEntradaProduto: EntradaProduto | undefined,
  dropdownProdutosServicos: ProdutoServicoDropDown[],
  centroCustoLista: CentroCustoDropDown[],
  onClose: () => void;
  onSubmit: (e: EntradaProdutoForm) => void;
  onOpenChangeHistory: (id: number, tabelaTipo: LogTipoTabela) => void;
};

export const EntradaProdutoModal = ({
  open, saving, loading, selectedEntradaProduto, dropdownProdutosServicos, centroCustoLista,
  onClose, onSubmit, onOpenChangeHistory
}: Props) => {

  const hookForm = useForm<EntradaProdutoForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(EntradaProdutoSchema),
  });

  const idProdutoServico = hookForm.watch("IdProdutoServico");
  useEffect(() => {
    const unidade = dropdownProdutosServicos.find(x => x.idProdutoServico === idProdutoServico)?.unidade;
    hookForm.setValue("Unidade", unidade ?? null);
  }, [hookForm, idProdutoServico, dropdownProdutosServicos]);

  useEffect(() => {
    if (open) {
      hookForm.reset();
      if (selectedEntradaProduto) {
        if (selectedEntradaProduto.Data) hookForm.setValue("Data", new Date(selectedEntradaProduto.Data))

        hookForm.setValue("IdCentroCusto", selectedEntradaProduto.IdCentroCusto);
        hookForm.setValue("IdEntradaProduto", selectedEntradaProduto.IdEntradaProduto);
        hookForm.setValue("IdProdutoServico", selectedEntradaProduto.IdProdutoServico);
        hookForm.setValue("IdFornecedor", selectedEntradaProduto.IdFornecedor);
        hookForm.setValue("NrNotaFiscal", selectedEntradaProduto.NrNotaFiscal);
        hookForm.setValue("Quantidade", selectedEntradaProduto.Quantidade ?? 0);
        hookForm.setValue("ValorUnitario", selectedEntradaProduto.ValorUnitario ?? null);
        hookForm.setValue("ValorTotal", selectedEntradaProduto.ValorTotal ?? null);
      }
    } else {
      hookForm.reset({ Data: new Date() });
    }
  }, [hookForm, open, selectedEntradaProduto]);

  const handleChangeHistory = useCallback(() => {
    if (selectedEntradaProduto) {
      onOpenChangeHistory(selectedEntradaProduto.IdEntradaProduto, LogTipoTabela.ENTRADA_PRODUTO);
    }
  }, [selectedEntradaProduto, onOpenChangeHistory]);

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        component: 'form',
        onSubmit: hookForm.handleSubmit(onSubmit)
      }}
      sx={{ "& .MuiDialog-paper": { maxWidth: "1200px", width: "100%" } }}
    >
      <DialogTitle>
        Entrada de Produtos
      </DialogTitle>

      <DialogContent>
        <FormContainer title={"Cadastrar/Editar"} arrangement="fullRow">
          <div className="flex flex-col gap-y-4 w-full pt-2">
            <div className="flex flex-row gap-4 w-full">
              <div className="w-[60%]">
                <DropDownProdutoServico
                  name='IdProdutoServico'
                  label='Produto'
                  required={true}
                  reactHookForm={hookForm}
                  tipo="P"
                />
              </div>

              <div className="w-[20%]">
                <FormDatePickerInput
                  name='Data'
                  label='Data'
                  reactHookForm={hookForm} />
              </div>

              <div className="w-[20%]">
                <DefaultFormInput
                  name="Unidade"
                  label="Unidade"
                  readOnly={true}
                  reactHookForm={hookForm}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DropDownFornecedor
                name='IdFornecedor'
                label='Fornecedor'
                reactHookForm={hookForm}
              />
              <DefaultFormAutocomplete
                name='IdCentroCusto'
                label='Centro de Custo'
                newButtonLabel='Novo'
                getOptionLabel={(x) => x.title}
                getOptionKey={(x) => x.id}
                options={centroCustoLista}
                reactHookForm={hookForm}
                required={true}
                onNew={() => { }}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <DefaultFormInput
                name="Quantidade"
                label="Quantidade"
                required={true}
                reactHookForm={hookForm}
              />
              <DefaultFormInput
                name="NrNotaFiscal"
                label="N° Nota Fiscal"
                reactHookForm={hookForm}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormMoneyMaskedInput
                name='ValorUnitario'
                label='Valor Unitário (R$)'
                largeNumbers={true}
                required={true}
                reactHookForm={hookForm}
              />
              <FormMoneyMaskedInput
                name='ValorTotal'
                label='Valor Total(R$)'
                largeNumbers={true}
                readOnly={true}
                reactHookForm={hookForm}
              />
            </div>


          </div>
        </FormContainer>
      </DialogContent>

      <DialogActions>
        <div className="w-full h-full items-center justify-between flex p-4 gap-4">
          <div>
            {
              isNotNullOrUndefined(selectedEntradaProduto) &&
              <PrimaryButton
                buttonColor="yellow"
                onClick={handleChangeHistory}>
                <span className="text-xs">
                  Historico de alterações
                </span>
              </PrimaryButton>
            }
          </div>

          <div className="flex flex-row gap-2">
            <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
              <h1
                className="text-sm text-black dark:text-white"
                onClick={onClose}>
                Cancelar
              </h1>
            </div>

            <div className="w-[120px]">
              {saving ? (
                <PrimaryButton
                  buttonColor="primary-green">
                  Salvando...
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  type="submit"
                  buttonColor="primary-green">
                  Salvar
                </PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </DialogActions>
    </Dialog>
  );
}